import { apiFetch } from '../auth/apiFetch.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// A hardware revision of a morphology (PRD-0007). `version` is the label an
// operator sees ('001', 'rev-b'); `id` is what a robot is pinned to.
export interface Revision {
  id: number;
  morphology_id: string;
  version: string;
  notes: string | null;
  meta: Record<string, unknown>;
  created_at: string;
}

export interface RobotRevisions {
  robot_id: string;
  morphology: string;
  current: number | null; // the pinned revision id
  revisions: Revision[];
}

async function fetchRobotRevisions(robotId: string): Promise<RobotRevisions> {
  const r = await apiFetch(
    `/api/robots/${encodeURIComponent(robotId)}/revisions`,
  );
  if (!r.ok) throw new Error(`status ${r.status}`);
  return (await r.json()) as RobotRevisions;
}

// The revisions available for a robot's morphology, plus the one it's pinned to.
export function useRobotRevisions(robotId: string | undefined) {
  return useQuery({
    queryKey: ['robot-revisions', robotId] as const,
    queryFn: () => fetchRobotRevisions(robotId as string),
    enabled: !!robotId,
  });
}

// Pin/confirm a robot's hardware revision (PRD-0007 V6). Invalidates the robot
// detail, its revision list, and the fleet list.
export function useSetRevision(robotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (morphologyVersionId: number): Promise<void> => {
      const r = await apiFetch(
        `/api/robots/${encodeURIComponent(robotId)}/revision`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ morphology_version_id: morphologyVersionId }),
        },
      );
      if (!r.ok) {
        const detail = (await r.json().catch(() => null)) as
          | { detail?: unknown }
          | null;
        const msg =
          typeof detail?.detail === 'string' ? detail.detail : `status ${r.status}`;
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['robot', robotId] });
      void qc.invalidateQueries({ queryKey: ['robot-revisions', robotId] });
      void qc.invalidateQueries({ queryKey: ['robots'] });
    },
  });
}
